import { Head, Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import OAuthButton from "@/components/OAuthButton"
import AppLogo from "@/components/app-logo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { newRegistrationPath, sessionPath } from "@/routes"

interface NewProps {
  lastAuthMethod?: string | null
}

export default function New({ lastAuthMethod }: NewProps) {
  const { data, setData, post, processing, errors } = useForm({
    email_address: "",
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(sessionPath())
  }

  return (
    <>
      <Head title="Sign In" />

      <div className="vignette noise bg-bg-body flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link href="/" className="inline-flex items-center justify-center">
              <AppLogo />
            </Link>
          </div>

          <div className="border-border bg-bg-card rounded-lg border p-8 shadow-lg">
            <h1 className="font-display mb-2 text-center text-2xl tracking-wide">
              Welcome back.
            </h1>

            <p className="text-bone-muted mb-6 text-center text-sm">
              Your tasks are waiting. Let&apos;s go.
            </p>

            <form onSubmit={handleSubmit} className="relative space-y-6">
              {lastAuthMethod === "email" && (
                <span className="bg-bg-body text-bone-muted border-border absolute -top-2 right-0 rounded border px-1.5 py-0.5 text-xs">
                  Last used
                </span>
              )}
              <div className="space-y-2">
                <Label htmlFor="email_address">Email address</Label>
                <Input
                  id="email_address"
                  type="email"
                  value={data.email_address}
                  onChange={(e) => setData("email_address", e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  autoComplete="username"
                />
                {errors.email_address && (
                  <p className="text-red text-sm">{errors.email_address}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={processing}>
                Get my code
              </Button>
            </form>

            <p className="text-bone-muted mt-4 text-center text-sm">
              We&apos;ll send a quick code. No password to remember.
            </p>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="border-border w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-bg-card text-bone-muted px-2">or</span>
              </div>
            </div>

            <div className="space-y-3">
              <OAuthButton
                provider="google_oauth2"
                action="sign_in"
                lastUsed={lastAuthMethod === "google_oauth2"}
              />
              <OAuthButton
                provider="apple"
                action="sign_in"
                lastUsed={lastAuthMethod === "apple"}
              />
            </div>

            <div className="mt-6 text-center">
              <p className="text-bone-muted text-sm">
                Don&apos;t have an account?{" "}
                <Link
                  href={newRegistrationPath()}
                  className="text-bone hover:underline"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
